import React, { useEffect, useMemo, useState } from "react";
import {
  listClasses,
  listSubjects,
  listStaff,
  TEACHER_DEPARTMENT,
  getClassSubjectsByWeek,
  setClassSubjectByWeek,
  clearClassSubjectByWeek,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import { exportPccmByWeekExcel } from "./pccmExport";

// "Phân công chuyên môn" RIÊNG theo tuần TKB đang chọn — sửa GV dạy/số tiết
// của 1 môn/lớp CHỈ cho tuần này, không đụng PCCM chung (classSubjects) đang
// dùng cho mọi tuần khác. Ô nào chưa đổi riêng thì vẫn hiện đúng giá trị
// PCCM chung (đọc từ "effective", server đã tự gộp) — bấm "Đổi riêng" mới
// tách ra ghi đè, bấm "Dùng lại PCCM chung" để xoá phần đã đổi riêng đó.
const HappySchoolPccmByWeek = ({ weekId, weekLabel }) => {
  const schoolId = getActiveSchoolId();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [base, setBase] = useState({});
  const [overrides, setOverrides] = useState({});
  const [effective, setEffective] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(""); // `${classId}_${subjectId}` đang lưu
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("class"); // "class" = theo lớp (sửa được) | "teacher" = theo giáo viên (xem)
  const [teacherFilter, setTeacherFilter] = useState("");

  const reload = () => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([
      listClasses(schoolId),
      listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT }),
      getClassSubjectsByWeek(schoolId, weekId),
    ])
      .then(([cls, subs, sf, cw]) => {
        setClasses((cls || []).slice().sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true })));
        setSubjects(subs || []);
        setStaff(sf || []);
        setBase(cw.base || {});
        setOverrides(cw.overrides || {});
        setEffective(cw.effective || {});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [schoolId, weekId]); // eslint-disable-line

  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s])), [subjects]);
  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);

  const cellKey = (classId, subjectId) => `${classId}_${subjectId}`;

  // Gom PCCM tuần này (effective = PCCM chung + phần đổi riêng) theo GIÁO VIÊN:
  // mỗi GV -> danh sách {lớp, môn, số tiết, có đổi riêng?} + tổng tiết.
  const byTeacher = useMemo(() => {
    const classOrder = Object.fromEntries(classes.map((c, i) => [c.id, i]));
    const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
    const groups = {};
    Object.entries(effective).forEach(([classId, subjMap]) => {
      if (!classById[classId]) return;
      Object.entries(subjMap || {}).forEach(([sid, rec]) => {
        const tid = (rec && rec.assignedTeacherId) || "";
        if (!groups[tid]) groups[tid] = [];
        groups[tid].push({
          classId, className: classById[classId].name, subjectId: sid,
          subjectName: (subjectById[sid] || {}).name || sid,
          periods: Number(rec && rec.periodsPerWeek) || 0,
          overridden: !!(overrides[classId] && overrides[classId][sid]),
        });
      });
    });
    return Object.entries(groups)
      .map(([tid, rows]) => {
        rows.sort((a, b) => (classOrder[a.classId] - classOrder[b.classId]) || a.subjectName.localeCompare(b.subjectName, "vi"));
        return {
          teacherId: tid,
          name: tid ? ((staffById[tid] || {}).name || `(GV không còn trong danh sách: ${tid})`) : "— Chưa phân công —",
          rows,
          totalPeriods: rows.reduce((sum, r) => sum + r.periods, 0),
          classCount: new Set(rows.map((r) => r.classId)).size,
        };
      })
      .sort((a, b) => (a.teacherId ? 0 : 1) - (b.teacherId ? 0 : 1) || a.name.localeCompare(b.name, "vi"));
  }, [effective, overrides, classes, subjectById, staffById]);

  const saveTeacher = async (classId, subjectId, teacherId) => {
    setSavingKey(cellKey(classId, subjectId));
    setError("");
    try {
      await setClassSubjectByWeek(schoolId, weekId, classId, subjectId, { assignedTeacherId: teacherId || null });
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey("");
    }
  };

  const savePeriods = async (classId, subjectId, periods) => {
    setSavingKey(cellKey(classId, subjectId));
    setError("");
    try {
      await setClassSubjectByWeek(schoolId, weekId, classId, subjectId, { periodsPerWeek: Number(periods) || 0 });
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey("");
    }
  };

  const resetCell = async (classId, subjectId) => {
    setSavingKey(cellKey(classId, subjectId));
    setError("");
    try {
      await clearClassSubjectByWeek(schoolId, weekId, classId, subjectId);
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey("");
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      // lấy TOÀN BỘ nhân sự (kể cả Phòng đào tạo — chủ nhiệm nhưng không dạy) để ghi đủ ngày sinh/CCCD/SĐT
      const staffAll = await listStaff(schoolId);
      await exportPccmByWeekExcel({ weekLabel, classes, subjects, staffAll: staffAll || [], effective });
    } catch (e) {
      setError(e.message || "Xuất Excel thất bại.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <p style={{ color: "#6b7280" }}>Đang tải...</p>;

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setView(key)}
      style={{
        padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", borderRadius: 6,
        border: view === key ? "1px solid #0ea5a3" : "1px solid #d1d5db",
        background: view === key ? "#ecfeff" : "#fff", color: view === key ? "#0f766e" : "#374151",
      }}
    >
      {label}
    </button>
  );

  const tabs = (
    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
      {tabBtn("class", "🏫 Theo lớp")}
      {tabBtn("teacher", "🧑‍🏫 Theo giáo viên")}
    </div>
  );

  if (view === "teacher") {
    const q = teacherFilter.trim().toLowerCase();
    const shown = q ? byTeacher.filter((t) => t.name.toLowerCase().includes(q)) : byTeacher;
    return (
      <div>
        {tabs}
        <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
          PCCM <b>{weekLabel}</b> xem theo giáo viên (đã gộp phần đổi riêng tuần này — dòng nền vàng).
          Muốn sửa thì chuyển sang tab <b>Theo lớp</b>.
        </p>
        <input
          value={teacherFilter}
          onChange={(e) => setTeacherFilter(e.target.value)}
          placeholder="🔍 Tìm giáo viên..."
          style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: 260, marginBottom: 12 }}
        />
        <span style={{ marginLeft: 10, fontSize: 13, color: "#6b7280" }}>{shown.filter((t) => t.teacherId).length} giáo viên</span>
        {shown.map((t) => (
          <div key={t.teacherId || "_none"} style={{ marginBottom: 14, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "8px 12px", background: t.teacherId ? "#f9fafb" : "#fef2f2", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: t.teacherId ? "#111827" : "#dc2626" }}>{t.name}</span>
              <span style={{ fontSize: 13, color: "#374151" }}>
                {t.classCount} lớp · <b>{t.totalPeriods}</b> tiết/tuần
              </span>
            </div>
            <div style={{ padding: "6px 12px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f1f5f9" }}>
              Lớp: <b>{[...new Set(t.rows.map((r) => r.className))].join(", ")}</b>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#6b7280" }}>
                  <th style={{ padding: "6px 12px", width: 120 }}>Lớp</th>
                  <th style={{ padding: "6px 12px" }}>Môn</th>
                  <th style={{ padding: "6px 12px", width: 110 }}>Số tiết/tuần</th>
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r) => (
                  <tr key={`${r.classId}_${r.subjectId}`} style={{ background: r.overridden ? "#fef9c3" : "transparent", borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "5px 12px", fontWeight: 600 }}>{r.className}</td>
                    <td style={{ padding: "5px 12px" }}>{r.subjectName}{r.overridden ? <span style={{ fontSize: 11, color: "#a16207", marginLeft: 6 }}>(đổi riêng tuần này)</span> : null}</td>
                    <td style={{ padding: "5px 12px" }}>{r.periods}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {shown.length === 0 && <p style={{ color: "#6b7280", fontSize: 13 }}>Không có giáo viên nào khớp.</p>}
      </div>
    );
  }

  return (
    <div>
      {tabs}
      <button type="button" onClick={exportExcel} disabled={exporting}
        style={{ marginBottom: 10, padding: "7px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
        {exporting ? "Đang xuất..." : "⬇ Export Excel"}
      </button>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Sửa GV dạy / số tiết ở đây chỉ áp dụng cho <b>đúng tuần này</b> — không đổi PCCM chung
        (không ảnh hưởng tuần khác). Ô có nền vàng là đã đổi riêng cho tuần này.
      </p>
      {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}
      {classes.map((cls) => {
        const subjMap = effective[cls.id] || {};
        const subjectIds = Object.keys(subjMap);
        if (!subjectIds.length) return null;
        return (
          <div key={cls.id} style={{ marginBottom: 20, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "8px 12px", background: "#f9fafb", fontWeight: 700, fontSize: 14 }}>{cls.name}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#6b7280" }}>
                  <th style={{ padding: "6px 12px" }}>Môn</th>
                  <th style={{ padding: "6px 12px" }}>Số tiết/tuần</th>
                  <th style={{ padding: "6px 12px" }}>Giáo viên dạy</th>
                  <th style={{ padding: "6px 12px" }}></th>
                </tr>
              </thead>
              <tbody>
                {subjectIds.map((sid) => {
                  const rec = subjMap[sid] || {};
                  const hasOverride = !!(overrides[cls.id] && overrides[cls.id][sid]);
                  const key = cellKey(cls.id, sid);
                  const busy = savingKey === key;
                  return (
                    <tr key={sid} style={{ background: hasOverride ? "#fef9c3" : "transparent", borderTop: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "6px 12px" }}>{(subjectById[sid] || {}).name || sid}</td>
                      <td style={{ padding: "6px 12px" }}>
                        <input
                          type="number"
                          min={0}
                          defaultValue={rec.periodsPerWeek || 0}
                          disabled={busy}
                          style={{ width: 56, padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: 6 }}
                          onBlur={(e) => {
                            const v = Number(e.target.value) || 0;
                            if (v !== (rec.periodsPerWeek || 0)) savePeriods(cls.id, sid, v);
                          }}
                        />
                      </td>
                      <td style={{ padding: "6px 12px" }}>
                        <select
                          value={rec.assignedTeacherId || ""}
                          disabled={busy}
                          style={{ padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: 6, minWidth: 180 }}
                          onChange={(e) => saveTeacher(cls.id, sid, e.target.value)}
                        >
                          <option value="">— Chưa phân công —</option>
                          {staff.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        {rec.assignedTeacherId && !staffById[rec.assignedTeacherId] && (
                          <span style={{ color: "#dc2626", fontSize: 12, marginLeft: 6 }}>(GV không còn trong danh sách)</span>
                        )}
                      </td>
                      <td style={{ padding: "6px 12px" }}>
                        {hasOverride && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => resetCell(cls.id, sid)}
                            style={{ fontSize: 12, padding: "3px 8px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer" }}
                          >
                            Dùng lại PCCM chung
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default HappySchoolPccmByWeek;
